import type { 
  WompiWebhookPayload, 
  WompiWebhookResponse,
  WompiApiResponse
} from "../types/types";
import { BASE_URL, request } from "../../../api/api";
import { 
  updateAmortizationPaymentStatusWithDetails,
  type PaymentStatusUpdateWithDetailsDto
} from "../../../api/amortization.api";

export class WompiWebhookService {
  /**
   * Handles incoming Wompi webhook events
   * @param payload - The webhook payload from Wompi
   * @returns Response indicating if the webhook was processed successfully
   */
  public static async handleWebhook(payload: WompiWebhookPayload): Promise<WompiWebhookResponse> {
    try {
      const amortizationId = this.extractAmortizationId(payload);
      
      if (!amortizationId) {
        return {
          processed: false,
          error: "No se pudo extraer el ID de amortización"
        };
      }

      // Determine payment status based on transaction result
      const status = this.mapTransactionResultToStatus(payload.resultadoTransaccion);
      
      // Handle different payment statuses
      switch (status) {
        case 'paid':
          await this.handlePaymentCompleted(amortizationId, payload);
          break;
        case 'failed':
          await this.handlePaymentFailed(amortizationId, payload);
          break;
        case 'cancelled':
          await this.handlePaymentCancelled(amortizationId, payload);
          break;
        default:
          console.warn("Estado de transacción no reconocido:", payload.resultadoTransaccion);
      }

      return { 
        processed: true, 
        amortizationId, 
        status 
      };
    } catch (error) {
      console.error("Error procesando webhook de Wompi:", error);
      return { 
        processed: false, 
        error: (error as Error).message 
      };
    }
  }

  /**
   * Extracts amortization ID from the webhook payload
   */
  private static extractAmortizationId(payload: WompiWebhookPayload): number | null {
    // Try to extract from identificadorEnlaceComercio (e.g., "AMR-123-timestamp")
    const comercioMatch = payload.enlacePago.identificadorEnlaceComercio.match(/^AMR-(\d+)-\d+$/);
    if (comercioMatch) {
      return parseInt(comercioMatch[1], 10);
    }

    // Try to extract from nombreProducto (e.g., "Pago de amortización #123")
    const productMatch = payload.enlacePago.nombreProducto.match(/amortización #(\d+)/i);
    if (productMatch) {
      return parseInt(productMatch[1], 10);
    }

    // Try to extract from aplicativo.id if it follows our pattern
    const appMatch = payload.aplicativo.id.match(/^AMR-(\d+)$/);
    if (appMatch) {
      return parseInt(appMatch[1], 10);
    }

    return null;
  }

  /**
   * Maps Wompi transaction result to our internal status
   */
  private static mapTransactionResultToStatus(resultado: string): 'paid' | 'failed' | 'cancelled' | 'pending' {
    const resultadoLower = resultado.toLowerCase();
    
    if (resultadoLower.includes('aprobad') || resultadoLower.includes('exitoso')) {
      return 'paid';
    } else if (resultadoLower.includes('cancelad') || resultadoLower.includes('anulad')) {
      return 'cancelled';
    } else if (resultadoLower.includes('rechazad') || resultadoLower.includes('fallid') || resultadoLower.includes('error')) {
      return 'failed';
    } else {
      return 'pending';
    }
  }

  /**
   * Handles successful payment completion
   */
  private static async handlePaymentCompleted(amortizationId: number, payload: WompiWebhookPayload) {
    try {
      // Update amortization status via API
      await this.updateAmortizationStatus(amortizationId, 'paid', payload);
    } catch (error) {
      console.error(`Error actualizando estado de amortización ${amortizationId}:`, error);
      throw error;
    }
  }

  /**
   * Handles failed payment
   */
  private static async handlePaymentFailed(amortizationId: number, payload: WompiWebhookPayload) {
    try {
      await this.updateAmortizationStatus(amortizationId, 'failed', payload);
    } catch (error) {
      console.error(`Error actualizando estado de amortización fallida ${amortizationId}:`, error);
      throw error;
    }
  }

  /**
   * Handles cancelled payment
   */
  private static async handlePaymentCancelled(amortizationId: number, payload: WompiWebhookPayload) {
    try {
      await this.updateAmortizationStatus(amortizationId, 'cancelled', payload);
    } catch (error) {
      console.error(`Error actualizando estado de amortización cancelada ${amortizationId}:`, error);
      throw error;
    }
  }

  /**
   * Updates amortization status in the backend
   */
  private static async updateAmortizationStatus(
    amortizationId: number, 
    status: string, 
    payload: WompiWebhookPayload
  ) {
    try {
      // Prepare payment status update with details
      const updateData: PaymentStatusUpdateWithDetailsDto = {
        amortizationId: amortizationId,
        status: status,
        paymentProvider: 'wompi',
        transactionId: payload.idTransaccion,
        authorizationCode: payload.codigoAutorizacion,
        amount: payload.monto,
        paymentDate: payload.fechaTransaccion,
        customerInfo: payload.cliente,
        providerPayload: payload // Store full payload for reference
      };

      // Call the new payment status endpoint
      await updateAmortizationPaymentStatusWithDetails(amortizationId, updateData);

    } catch (error) {
      console.error(`Error en API al actualizar amortización ${amortizationId}:`, error);
      throw error;
    }
  }

  /**
   * Validates webhook signature (if Wompi provides one)
   * This should be implemented based on Wompi's security requirements
   */
  public static validateWebhookSignature(payload: WompiWebhookPayload, signature?: string): boolean {
    console.info("Validating webhook signature for:", payload.idTransaccion, "with signature:", signature);
    return true;
  }

  /**
   * Get payment status from Wompi API
   */
  public static async getPaymentStatus(amortizationId: number): Promise<WompiApiResponse> {
    try {
      const response = await request<WompiApiResponse>(`${BASE_URL}/api/WompiPayment/payment-status/${amortizationId}`, {
        method: 'GET'
      });
      return response;
    } catch (error) {
      console.error(`Error obteniendo estado de pago para amortización ${amortizationId}:`, error);
      throw error;
    }
  }
}
